import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { Box, Text } from "@twilio-paste/core";
import { ConversationDto } from "../../../dto/conversation";
import { AppState } from "../../../store";
import { actionCreators } from "../../../store";
import { ConversationListItem } from "./ConversationListItem";
import { useNavigate, useParams } from "react-router-dom";
import { unexpectedErrorNotification } from "../../../helpers";
import { SignalRStatusBadge } from "../../../signalr/SignalRStatusBadge";
import { getConversationLockState } from "../../../hooks/useConversationLockState";

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <Box
    paddingX="space50"
    paddingY="space30"
    borderBottomWidth="borderWidth10"
    borderBottomStyle="solid"
    borderBottomColor="colorBorderWeak"
    backgroundColor="colorBackgroundBody"
    position="sticky"
    top="0"
    zIndex={"zIndex10"}
  >
    <Text as="h4" fontWeight="fontWeightSemibold" color="colorTextWeak">
      {title}
    </Text>
  </Box>
);

type Props = {
  viewMode?: "all" | "unread" | null;
  hideMarketo?: boolean;
};

const NewConversationsList: React.FC<Props> = ({
  viewMode = null,
  hideMarketo = true,
}) => {
  const dispatch = useDispatch();

  const { getConversations, updateCurrentConversation, addNotifications } =
    bindActionCreators(actionCreators, dispatch);

  const navigate = useNavigate();

  const { items: conversations } = useSelector(
    (state: AppState) => state.conversations
  );

  const messages = useSelector((state: AppState) => state.messages);

  const operator = useSelector((state: AppState) => state.operator);
  const roles = useSelector((state: AppState) => state.roles);

  const myOperatorId = operator?.id ?? null;

  const isSupervisor = useMemo(() => {
    if (!operator) return false;
    const role = roles?.find((r) => r.id === operator.roleId);
    return operator.roleName === "Supervisor" || !!role?.canViewAll;
  }, [operator, roles]);

  const { twilioSid: selectedSid } = useParams<{ twilioSid: string }>();
  const currentSid = useSelector((state: AppState) => state.sid);
  const didFetchRef = useRef(false);

  useEffect(() => {
    if (didFetchRef.current) return;
    didFetchRef.current = true;

    (async () => {
      try {
        await getConversations();
      } catch (e: any) {
        unexpectedErrorNotification(
          e?.message ?? "Errore lettura conversazioni.",
          addNotifications
        );
      }
    })();
  }, [getConversations, addNotifications]);

  const uniqueConversations = useMemo(() => {
    const list = conversations ?? [];
    const map = new Map<string, ConversationDto>();

    for (const c of list) {
      const key = c.twilioSid ?? c.id;
      if (!key) continue;
      if (!map.has(key)) {
        map.set(key, c);
      }
    }

    return Array.from(map.values());
  }, [conversations]);

  const filteredConversations = useMemo(() => {
    let list = uniqueConversations;

    if (viewMode === "unread") {
      list = list.filter((c) => (c.unreadMessages ?? 0) > 0);
    }

    if (hideMarketo) {
      list = list.filter((c) => {
        const sid = c.twilioSid;
        if (!sid) return true;
        const msgs = messages[sid];
        if (!msgs || msgs.length === 0) return true;
        const lastAuthor = (msgs[msgs.length - 1].author ?? "").toLowerCase();
        return !lastAuthor.includes("marketo");
      });
    }

    return list;
  }, [uniqueConversations, viewMode, hideMarketo, messages]);

  // Auto load
  useEffect(() => {
    if (!selectedSid) return;
    if (!uniqueConversations || uniqueConversations.length === 0) return;
    if (currentSid === selectedSid) return;

    const convo = uniqueConversations.find((c) => c.twilioSid === selectedSid);

    if (!convo) {
      console.warn(
        "twilioSid non trovato nella lista conversazioni:",
        selectedSid
      );
      return;
    }

    const { canOpen } = getConversationLockState({
      convo,
      myOperatorId,
      isSupervisor,
    });

    if (!canOpen) {
      unexpectedErrorNotification(
        "Questa conversazione è attualmente bloccata da un altro utente.",
        addNotifications
      );
      return;
    }

    updateCurrentConversation(selectedSid);
  }, [
    selectedSid,
    uniqueConversations,
    currentSid,
    myOperatorId,
    isSupervisor,
    updateCurrentConversation,
    addNotifications,
  ]);

  const handleConversationClick = (convo: ConversationDto) => {
    const sid = convo.twilioSid;

    if (!sid) {
      console.warn("Nessun twilioSid per questa conversazione", convo);
      return;
    }

    const { canOpen } = getConversationLockState({
      convo,
      myOperatorId,
      isSupervisor,
    });

    if (!canOpen) {
      unexpectedErrorNotification(
        "Questa conversazione è attualmente bloccata da un altro utente.",
        addNotifications
      );
      return;
    }

    updateCurrentConversation(sid);
    navigate(`/conversations/${sid}`);
  };

  const { myConvos, otherConvos } = useMemo(() => {
    const items = filteredConversations ?? [];

    if (!myOperatorId)
      return { myConvos: [] as ConversationDto[], otherConvos: items };

    const my: ConversationDto[] = [];
    const other: ConversationDto[] = [];

    for (const c of items) {
      if (c.operatorId && c.operatorId === myOperatorId) my.push(c);
      else other.push(c);
    }

    return { myConvos: my, otherConvos: other };
  }, [filteredConversations, myOperatorId]);

  const isEmpty = (filteredConversations?.length ?? 0) === 0;

  return (
    <Box
      as="section"
      aria-label="Lista conversazioni"
      overflowY="auto"
      height="100%"
    >
      {isEmpty ? (
        <Box padding="space60">
          <Text as="p" color="colorTextWeak">
            Nessuna conversazione trovata.
          </Text>
        </Box>
      ) : (
        <>
          {/* LE MIE CHAT */}
          <SectionHeader title="LE MIE CHAT" />
          {myConvos.length === 0 ? (
            <Box padding="space50">
              <Text as="p" color="colorTextWeak">
                Nessuna conversazione assegnata a te.
              </Text>
            </Box>
          ) : (
            myConvos.map((convo) => (
              <ConversationListItem
                key={convo.twilioSid ?? convo.id}
                convo={convo}
                onClick={handleConversationClick}
                isSelected={!!selectedSid && convo.twilioSid === selectedSid}
                myOperatorId={myOperatorId}
                isSupervisor={isSupervisor}
              />
            ))
          )}

          {/* ALTRE CHAT */}
          <SectionHeader title="ALTRE CHAT" />
          {otherConvos.length === 0 ? (
            <Box padding="space50">
              <Text as="p" color="colorTextWeak">
                Nessuna conversazione disponibile.
              </Text>
            </Box>
          ) : (
            otherConvos.map((convo) => (
              <ConversationListItem
                key={convo.twilioSid ?? convo.id}
                convo={convo}
                onClick={handleConversationClick}
                isSelected={!!selectedSid && convo.twilioSid === selectedSid}
                myOperatorId={myOperatorId}
                isSupervisor={isSupervisor}
              />
            ))
          )}
        </>
      )}
    </Box>
  );
};

export default NewConversationsList;
